// Dashboard Portal
// pages/UserProfile/Profile.js
import React, { useState, useEffect, forwardRef, useImperativeHandle, useRef, useCallback } from "react";
import Select from "react-select";
import { Visibility, VisibilityOff } from '@mui/icons-material';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import MarkunreadMailboxOutlinedIcon from '@mui/icons-material/MarkunreadMailboxOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';
import {
  IconButton, InputAdornment, TextField, Box, Divider, Typography,
  Button, Alert, Checkbox, FormControlLabel, Link as MuiLink, useTheme, CircularProgress,
  Avatar,
} from '@mui/material';

import axiosInstance from "../../../utils/axiosInstance";

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const colors = {
  textPrimary: '#1e293b',
  textSecondary: '#475569',
  textTertiary: '#94a3b8',
};

const ProfilePage = forwardRef((props, ref) => {
    const { includePasswordUpdate = false, isSetupMode = false } = props;
    const theme = useTheme();

    // ============ STATE DECLARATIONS ============
    const [UserProfile, setUserProfile] = useState("");
    const [UserfName, setUserfName] = useState("");
    const [UserlName, setUserlName] = useState("");
    const [UserEmail, setUserEmail] = useState("");
    const [UserAddress, setUserAddress] = useState("");
    const [UserCountry, setUserCountry] = useState("");
    const [UserCity, setUserCity] = useState("");
    const [UserState, setUserState] = useState("");
    const [UserZip, setUserZip] = useState("");
    const [message, setMessage] = useState("");
    const [selectedFile, setSelectedFile] = useState(null);
    const [isFormValid, setIsFormValid] = useState(false);

    // Country/State/City selection states
    const [countryOptions, setCountryOptions] = useState([]);
    const [stateOptions, setStateOptions] = useState([]);
    const [cityOptions, setCityOptions] = useState([]);
    const [loadingStates, setLoadingStates] = useState(false);
    const [loadingCities, setLoadingCities] = useState(false);

    const [subscriptionEmail, setSubscriptionEmail] = useState("");
    const [subscriptionPhone, setSubscriptionPhone] = useState("");
    const [promotionalConsent, setPromotionalConsent] = useState(false);
    const [exclusiveDealsConsent, setExclusiveDealsConsent] = useState(false);
    const [termsConsent, setTermsConsent] = useState(false);
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [subscriptionMessage, setSubscriptionMessage] = useState("");
    const [isSubscribing, setIsSubscribing] = useState(false);
    const [emailMismatchWarning, setEmailMismatchWarning] = useState("");

    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [passwordError, setPasswordError] = useState("");

    // ============ REF DECLARATIONS ============
    const stateFetchIdRef = useRef(0);
    const stateSearchTimeoutRef = useRef(null);
    const cityFetchIdRef = useRef(0);
    const citySearchTimeoutRef = useRef(null);
    const selectedCountryIdRef = useRef(null);
    const selectedStateRef = useRef(null);

    // ============ STYLE DEFINITIONS ============
    const inputSx = {
        '& .MuiOutlinedInput-root': {
            borderRadius: '14px', fontFamily, bgcolor: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(8px)',
            border: 'none', outline: 'none',
            '& .MuiOutlinedInput-notchedOutline': { display: 'none !important' },
            '& fieldset': { display: 'none !important' },
            '&.Mui-focused': { bgcolor: 'rgba(255,255,255,1)', boxShadow: `0 0 0 2px ${theme.palette.primary.main}40` },
            '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
        },
        '& .MuiInputBase-input': {
            fontFamily, fontSize: '0.875rem', py: '0.75rem', px: '0.5rem',
            border: 'none', outline: 'none',
            '&:focus': { outline: 'none', boxShadow: 'none' },
            '&::placeholder': { color: colors.textTertiary, opacity: 1 },
        },
    };

    const selectCustomStyles = {
        control: (base, state) => ({
            ...base,
            borderRadius: '14px',
            fontFamily,
            backgroundColor: 'rgba(255,255,255,0.7)',
            backdropFilter: 'blur(8px)',
            border: state.isFocused ? 'none' : 'none',
            boxShadow: state.isFocused ? `0 0 0 2px ${theme.palette.primary.main}40` : 'none',
            minHeight: '48px',
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.9)' },
        }),
        placeholder: (base) => ({ ...base, color: colors.textTertiary, fontFamily, fontSize: '0.875rem' }),
        singleValue: (base) => ({ ...base, fontFamily, fontSize: '0.875rem', color: colors.textPrimary }),
        menu: (base) => ({ ...base, borderRadius: '14px', overflow: 'hidden', fontFamily, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' }),
        option: (base, state) => ({
            ...base, fontFamily, fontSize: '0.875rem',
            backgroundColor: state.isSelected ? theme.palette.primary.main : state.isFocused ? 'rgba(0,0,0,0.04)' : 'transparent',
        }),
    };

    // ============ DATA FETCHING FUNCTIONS ============
    // Fetch countries on mount
    const fetchCountries = async () => {
        try {
            const response = await axiosInstance.get(`/api/selections/countries`);
            setCountryOptions(response.data.data || []);
        } catch (error) {
            console.error("Failed to fetch countries:", error);
        }
    };

    // Fetch states by country ID with optional search term
    const fetchStatesByCountry = useCallback(async (countryId, searchTerm = '') => {
        if (!countryId) {
            setStateOptions([]);
            return;
        }
        const fetchId = ++stateFetchIdRef.current;
        setLoadingStates(true);
        try {
            const params = searchTerm ? { search: searchTerm } : {};
            const response = await axiosInstance.get(`/api/selections/countries/${countryId}/states`, { params });
            if (fetchId === stateFetchIdRef.current && response.data.success) {
                setStateOptions(response.data.data || []);
            }
        } catch (error) {
            if (fetchId === stateFetchIdRef.current) {
                console.error("Failed to fetch states:", error);
                setStateOptions([]);
            }
        } finally {
            if (fetchId === stateFetchIdRef.current) {
                setLoadingStates(false);
            }
        }
    }, []);

    // Fetch cities by country ID with optional search term and state filter
    const fetchCitiesByCountry = useCallback(async (countryId, searchTerm = '', stateFilter = '') => {
        if (!countryId) {
            setCityOptions([]);
            return;
        }
        const fetchId = ++cityFetchIdRef.current;
        setLoadingCities(true);
        try {
            const params = {};
            if (searchTerm) params.search = searchTerm;
            if (stateFilter) params.state = stateFilter;
            const response = await axiosInstance.get(`/api/selections/countries/${countryId}/cities`, { params });
            if (fetchId === cityFetchIdRef.current && response.data.success) {
                setCityOptions(response.data.data || []);
            }
        } catch (error) {
            if (fetchId === cityFetchIdRef.current) {
                console.error("Failed to fetch cities:", error);
                setCityOptions([]);
            }
        } finally {
            if (fetchId === cityFetchIdRef.current) {
                setLoadingCities(false);
            }
        }
    }, []);

    // Debounced state search
    const triggerStateSearch = useCallback((searchTerm) => {
        if (stateSearchTimeoutRef.current) {
            clearTimeout(stateSearchTimeoutRef.current);
        }
        const countryId = selectedCountryIdRef.current;
        if (!countryId) return;
        if (searchTerm.length > 0) {
            stateFetchIdRef.current++;
            setStateOptions([]);
            setLoadingStates(true);
        }
        stateSearchTimeoutRef.current = setTimeout(() => {
            fetchStatesByCountry(countryId, searchTerm);
        }, 150);
    }, [fetchStatesByCountry]);

    // Debounced city search
    const triggerCitySearch = useCallback((searchTerm) => {
        if (citySearchTimeoutRef.current) {
            clearTimeout(citySearchTimeoutRef.current);
        }
        const countryId = selectedCountryIdRef.current;
        const stateFilter = selectedStateRef.current;
        if (!countryId) return;
        if (searchTerm.length > 0) {
            cityFetchIdRef.current++;
            setCityOptions([]);
            setLoadingCities(true);
        }
        citySearchTimeoutRef.current = setTimeout(() => {
            fetchCitiesByCountry(countryId, searchTerm, stateFilter);
        }, 150);
    }, [fetchCitiesByCountry]);

    const fetchData = () => {
        axiosInstance
            .get(`/get-user-profile`)
            .then((response) => {
                const responseData = typeof response.data.data === 'string' ? JSON.parse(response.data.data) : response.data.data;
                if (responseData.length > 0){
                    let profile_data = responseData[0]
                    setUserProfile(profile_data.profile_image);
                    setUserfName(profile_data.fname);
                    setUserlName(profile_data.lname);
                    setUserEmail(profile_data.email);
                    setUserAddress(profile_data.address_one);
                    setUserCountry(profile_data.country);
                    setUserCity(profile_data.city);
                    setUserState(profile_data.state);
                    setUserZip(profile_data.zip);
                    setSubscriptionEmail(profile_data.email);
                }else{
                    const user = JSON.parse(localStorage.getItem("user"));
                    if(Object.keys(user).length > 0){
                        setUserEmail(user.user_email);
                        setSubscriptionEmail(user.user_email);
                    }
                }
            })
            .catch((error) => {
                setMessage(error.response?.data?.message || "Error fetching user profile");
            });
    };

    // ============ USE EFFECTS ============
    useEffect(() => {
        document.title = 'Profile | KlonIT Web Application';
        fetchCountries();
        fetchData();
    }, []);

    // Cleanup timeouts on unmount
    useEffect(() => {
        return () => {
            if (citySearchTimeoutRef.current) clearTimeout(citySearchTimeoutRef.current);
            if (stateSearchTimeoutRef.current) clearTimeout(stateSearchTimeoutRef.current);
        };
    }, []);

    // Fetch states when country options are loaded and user has a country
    useEffect(() => {
        if (UserCountry && countryOptions.length > 0) {
            const countryObj = countryOptions.find((c) => c.value === UserCountry);
            if (countryObj?.id) {
                selectedCountryIdRef.current = countryObj.id;
                fetchStatesByCountry(countryObj.id, '');
            }
        }
    }, [UserCountry, countryOptions, fetchStatesByCountry]);

    // Fetch cities when state options are loaded and user has a state
    useEffect(() => {
        if (UserState && stateOptions.length > 0 && selectedCountryIdRef.current) {
            selectedStateRef.current = UserState;
            fetchCitiesByCountry(selectedCountryIdRef.current, '', UserState);
        }
    }, [UserState, stateOptions, fetchCitiesByCountry]);

    // Form validation effect
    useEffect(() => {
        let isValid = UserfName !== "" &&
            UserlName !== "" &&
            UserEmail !== "" &&
            UserAddress !== "" &&
            UserCountry !== "" &&
            UserCity !== "" &&
            UserState !== "" &&
            UserZip !== "";

        if (includePasswordUpdate) {
            isValid = isValid &&
                        newPassword !== "" &&
                        newPassword.length >= 8 &&
                        newPassword === confirmPassword;
        }

        setIsFormValid(isValid);
        props.onFormValidityChange && props.onFormValidityChange(isValid);
    }, [UserfName, UserlName, UserEmail, UserAddress, UserCountry, UserCity, UserState, UserZip, newPassword, confirmPassword, includePasswordUpdate, props]);

    // Email mismatch warning effect
    useEffect(() => {
        if (subscriptionEmail && UserEmail && subscriptionEmail !== UserEmail) {
            setEmailMismatchWarning("This email is different from your account email. Please use your signup email for subscription.");
        } else {
            setEmailMismatchWarning("");
        }
    }, [subscriptionEmail, UserEmail]);

    // ============ HELPER FUNCTIONS ============
    const isJoinEnabled = () => {
        const hasContact = subscriptionEmail || subscriptionPhone;
        const emailValid = !subscriptionEmail || (subscriptionEmail === UserEmail);
        const hasConsents = (promotionalConsent || exclusiveDealsConsent) && termsConsent;
        return hasContact && emailValid && hasConsents && !isSubscribed;
    };

    const handleCountryChange = (selectedOption) => {
        setUserCountry(selectedOption ? selectedOption.value : "");
        setUserState("");
        setUserCity("");

        if (citySearchTimeoutRef.current) clearTimeout(citySearchTimeoutRef.current);
        if (stateSearchTimeoutRef.current) clearTimeout(stateSearchTimeoutRef.current);

        selectedCountryIdRef.current = selectedOption?.id || null;
        selectedStateRef.current = null;

        if (selectedOption?.id) {
            fetchStatesByCountry(selectedOption.id, '');
            setCityOptions([]);
        } else {
            setStateOptions([]);
            setCityOptions([]);
        }
    };

    const handleStateChange = (selectedOption) => {
        setUserState(selectedOption ? selectedOption.value : "");
        setUserCity("");

        if (citySearchTimeoutRef.current) clearTimeout(citySearchTimeoutRef.current);

        selectedStateRef.current = selectedOption?.value || null;

        const countryId = selectedCountryIdRef.current;
        if (countryId && selectedOption?.value) {
            fetchCitiesByCountry(countryId, '', selectedOption.value);
        } else if (countryId) {
            fetchCitiesByCountry(countryId, '', '');
        } else {
            setCityOptions([]);
        }
    };

    const handleCityChange = (selectedOption) => {
        setUserCity(selectedOption ? selectedOption.label : "");
    };

    const getDefaultCountryOption = () => {
        return countryOptions.find(option => option.value === UserCountry);
    };

    const getDefaultStateOption = () => {
        return stateOptions.find(option => option.value === UserState);
    };

    const getDefaultCityOption = () => {
        return cityOptions.find(option => option.label === UserCity);
    };

    const updateUserProfileToLocalStorage = (data) => {
        const userStr = localStorage.getItem('user');
        if (userStr) {
            const userData = JSON.parse(userStr);
            userData.profile = data || null;
            localStorage.setItem('user', JSON.stringify(userData));
            window.dispatchEvent(new CustomEvent('userLocalStorageUpdated', { detail: userData  }));
            console.log("Local storage user profile updated.", data);
        }
    }

    const validatePassword = () => {
        if (!includePasswordUpdate) return true;

        if (newPassword || confirmPassword) {
            if (!newPassword) {
                setPasswordError("New password is required");
                return false;
            }
            if (newPassword.length < 8) {
                setPasswordError("New password must be at least 8 characters");
                return false;
            }
            if (newPassword !== confirmPassword) {
                setPasswordError("Passwords do not match");
                return false;
            }
        }

        setPasswordError("");
        return true;
    };

    const handlePasswordUpdate = async () => {
        if (!newPassword && !confirmPassword) {
            return true;
        }

        try {
            const response = await axiosInstance.post(`/update-password`, {
                new_password: newPassword,
                confirm_password: confirmPassword,
                is_initial_setup: isSetupMode
            });

            if (response.data.success) {
                setNewPassword("");
                setConfirmPassword("");
                return true;
            } else {
                setPasswordError(response.data.message || "Failed to update password");
                return false;
            }
        } catch (error) {
            setPasswordError(error.response?.data?.message || "Error updating password");
            return false;
        }
    };

    const handleJoinSubscription = async () => {
        if (!isJoinEnabled()) {
            return;
        }

        setIsSubscribing(true);
        setSubscriptionMessage("");

        try {
            const response = await axiosInstance.post('/api/subscribe', {
                email: subscriptionEmail || null,
                phone: subscriptionPhone || null,
                promotional_consent: promotionalConsent,
                exclusive_deals_consent: exclusiveDealsConsent,
                terms_consent: termsConsent
            });

            if (response.data.success) {
                const consentTypes = [];
                if (promotionalConsent) consentTypes.push("promotional offers");
                if (exclusiveDealsConsent) consentTypes.push("exclusive deals");

                const consentText = consentTypes.join(" and ");
                const channels = [];
                if (subscriptionEmail) channels.push("email");
                if (subscriptionPhone) channels.push("SMS");
                const channelText = channels.join(" and ");

                setSubscriptionMessage(`Successfully subscribed to ${consentText} via ${channelText}!`);
                setIsSubscribed(true);

                setSubscriptionEmail("");
                setSubscriptionPhone("");
                setPromotionalConsent(false);
                setExclusiveDealsConsent(false);
                setTermsConsent(false);
            }

        } catch (error) {
            console.error("Subscription error:", error);

            if (error.response?.data?.message) {
                setSubscriptionMessage(error.response.data.message);
            } else if (error.response?.status === 409) {
                setSubscriptionMessage("You are already subscribed with this email or phone number.");
            } else if (error.response?.status === 400) {
                setSubscriptionMessage("Invalid input. Please check your information.");
            } else {
                setSubscriptionMessage("An error occurred. Please try again later.");
            }
        } finally {
            setIsSubscribing(false);
        }
    };

    const handleSaveButtonClick = async () => {
        if (!validatePassword()) {
            setMessage(passwordError);
            props.onError && props.onError(passwordError);
            return;
        }

        if (includePasswordUpdate && (newPassword || confirmPassword)) {
            const passwordUpdated = await handlePasswordUpdate();
            if (!passwordUpdated) {
                setMessage(passwordError);
                props.onError && props.onError(passwordError);
                return;
            }
        }

        if (selectedFile) {
            const validExtensions = ['image/jpeg', 'image/jpg', 'image/png'];
            if (!validExtensions.includes(selectedFile.type)) {
                setMessage("Invalid file type. Only JPG, JPEG, and PNG are allowed.");
                props.onError && props.onError("Invalid file type");
                return;
            }

            const formData = new FormData();
            formData.append('avatar', selectedFile);
            formData.append('fname', UserfName);
            formData.append('lname', UserlName);
            formData.append('address_one', UserAddress);
            formData.append('country', UserCountry);
            formData.append('city', UserCity);
            formData.append('state', UserState);
            formData.append('zip', UserZip);

            await axiosInstance.post(`/update-user-profile`, formData)
                .then((response) => {
                    if(response.data.success){
                        const responseData = typeof response.data.data === 'string' ? JSON.parse(response.data.data) : response.data.data;
                        setMessage("Profile updated successfully!");
                        updateUserProfileToLocalStorage(responseData);
                        props.onComplete && props.onComplete(response.data);
                    } else {
                        setMessage(response.data.message);
                        props.onError && props.onError(response.data);
                    }
                })
                .catch((error) => {
                    const errorMsg = error.response?.data?.message || "Error updating profile.";
                    setMessage(errorMsg);
                    props.onError && props.onError(errorMsg);
                });
        } else {
            const data = {
                fname: UserfName,
                lname: UserlName,
                address_one: UserAddress,
                country: UserCountry,
                city: UserCity,
                state: UserState,
                zip: UserZip
            };

            await axiosInstance.post(`/update-user-profile`, data)
                .then((response) => {
                    if(response.data.success){
                        const responseData = typeof response.data.data === 'string' ? JSON.parse(response.data.data) : response.data.data;
                        updateUserProfileToLocalStorage(responseData);
                        setMessage(response.data.message);

                        const nextPath =  "/accept-terms";
                        props.onComplete && props.onComplete(nextPath);
                    } else {
                        setMessage(response.data.message);
                        props.onError && props.onError(response.data);
                    }
                })
                .catch((error) => {
                    const errorMsg = error.response?.data?.message || "Error updating profile.";
                    setMessage(errorMsg);
                    props.onError && props.onError(errorMsg);
                });
        }
    };

    const [previewUrl, setPreviewUrl] = useState(null);

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setSelectedFile(file);
            setPreviewUrl(URL.createObjectURL(file));
        }
    };

    useImperativeHandle(ref, () => ({
        handleSaveButtonClick,
    }));

    const SectionLabel = ({ children, required }) => (
        <Typography sx={{ fontFamily, fontWeight: 500, fontSize: '0.8rem', color: colors.textSecondary, mb: 0.75 }}>
            {children} {required && <Box component="span" sx={{ color: '#dc2626' }}>*</Box>}
        </Typography>
    );

    return (
        <Box sx={{ fontFamily }}>
            {/* Profile Picture */}
            <Box sx={{ mb: 2.5, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                <Box
                    component="label"
                    sx={{
                        position: 'relative',
                        width: 90, height: 90,
                        borderRadius: '50%',
                        cursor: 'pointer',
                        '&:hover .camera-overlay': { opacity: 1 },
                    }}
                >
                    <Avatar
                        src={previewUrl || UserProfile || 'images/avatars/default.png'}
                        sx={{
                            width: 90, height: 90,
                            border: '3px solid',
                            borderColor: previewUrl || (UserProfile && UserProfile !== 'images/avatars/default.png') ? `${theme.palette.primary.main}30` : 'rgba(0,0,0,0.06)',
                            transition: 'all 0.25s ease',
                        }}
                    />
                    <Box
                        className="camera-overlay"
                        sx={{
                            position: 'absolute',
                            top: 0, left: 0, right: 0, bottom: 0,
                            borderRadius: '50%',
                            bgcolor: 'rgba(0,0,0,0.45)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            opacity: 0,
                            transition: 'opacity 0.2s ease',
                        }}
                    >
                        <CameraAltOutlinedIcon sx={{ color: '#fff', fontSize: 28 }} />
                    </Box>
                    <input type="file" hidden accept="image/jpeg, image/jpg, image/png" onChange={handleFileChange} />
                </Box>
                <Typography sx={{ fontFamily, fontSize: '0.75rem', color: colors.textTertiary, lineHeight: 1.4 }}>
                    {selectedFile ? selectedFile.name : 'Click to upload JPG or PNG'}
                </Typography>
            </Box>

            {/* Name Row */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                    <SectionLabel required>First Name</SectionLabel>
                    <TextField
                        fullWidth placeholder="First name" value={UserfName}
                        onChange={(e) => setUserfName(e.target.value)}
                        InputProps={{ startAdornment: <InputAdornment position="start"><PersonOutlinedIcon sx={{ fontSize: '1.1rem', color: colors.textTertiary }} /></InputAdornment> }}
                        sx={inputSx}
                    />
                </Box>
                <Box sx={{ flex: 1 }}>
                    <SectionLabel required>Last Name</SectionLabel>
                    <TextField
                        fullWidth placeholder="Last name" value={UserlName}
                        onChange={(e) => setUserlName(e.target.value)}
                        InputProps={{ startAdornment: <InputAdornment position="start"><PersonOutlinedIcon sx={{ fontSize: '1.1rem', color: colors.textTertiary }} /></InputAdornment> }}
                        sx={inputSx}
                    />
                </Box>
            </Box>

            {/* Email */}
            <Box sx={{ mb: 2 }}>
                <SectionLabel required>Email</SectionLabel>
                <TextField
                    fullWidth placeholder="Email" value={UserEmail} disabled
                    InputProps={{ startAdornment: <InputAdornment position="start"><EmailOutlinedIcon sx={{ fontSize: '1.1rem', color: colors.textTertiary }} /></InputAdornment> }}
                    sx={{ ...inputSx, '& .MuiOutlinedInput-root': { ...inputSx['& .MuiOutlinedInput-root'], opacity: 0.6 } }}
                />
            </Box>

            {/* Address */}
            <Box sx={{ mb: 2 }}>
                <SectionLabel required>Address</SectionLabel>
                <TextField
                    fullWidth placeholder="Street address" value={UserAddress}
                    onChange={(e) => setUserAddress(e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start"><HomeOutlinedIcon sx={{ fontSize: '1.1rem', color: colors.textTertiary }} /></InputAdornment> }}
                    sx={inputSx}
                />
            </Box>

            {/* Country & State */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                    <SectionLabel required>Country</SectionLabel>
                    <Select
                        options={countryOptions}
                        value={getDefaultCountryOption()}
                        onChange={handleCountryChange}
                        isClearable
                        styles={selectCustomStyles}
                        placeholder="Select country..."
                    />
                </Box>
                <Box sx={{ flex: 1 }}>
                    <SectionLabel required>State/Province</SectionLabel>
                    <Select
                        options={stateOptions}
                        value={getDefaultStateOption()}
                        onChange={handleStateChange}
                        onInputChange={(inputValue, { action }) => {
                            if (action === 'input-change') {
                                triggerStateSearch(inputValue);
                            }
                        }}
                        isClearable
                        isLoading={loadingStates}
                        isDisabled={!UserCountry}
                        styles={selectCustomStyles}
                        placeholder={!UserCountry ? "Select country first" : "Type to search..."}
                        noOptionsMessage={() => !UserCountry ? "Select a country first" : loadingStates ? "Loading..." : "No states found"}
                        filterOption={() => true}
                    />
                </Box>
            </Box>

            {/* City & Zip */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                    <SectionLabel required>City</SectionLabel>
                    <Select
                        options={cityOptions}
                        value={getDefaultCityOption()}
                        onChange={handleCityChange}
                        onInputChange={(inputValue, { action }) => {
                            if (action === 'input-change') {
                                triggerCitySearch(inputValue);
                            }
                        }}
                        isClearable
                        isLoading={loadingCities}
                        isDisabled={!UserCountry}
                        styles={selectCustomStyles}
                        placeholder={!UserCountry ? "Select country first" : "Type to search..."}
                        noOptionsMessage={() => !UserCountry ? "Select a country first" : loadingCities ? "Loading..." : "No cities found"}
                        filterOption={() => true}
                    />
                </Box>
                <Box sx={{ flex: 1 }}>
                    <SectionLabel required>Zip Code</SectionLabel>
                    <TextField
                        fullWidth placeholder="Zip code" value={UserZip}
                        onChange={(e) => setUserZip(e.target.value)}
                        InputProps={{ startAdornment: <InputAdornment position="start"><MarkunreadMailboxOutlinedIcon sx={{ fontSize: '1.1rem', color: colors.textTertiary }} /></InputAdornment> }}
                        sx={inputSx}
                    />
                </Box>
            </Box>

            {/* Password Update Section */}
            {includePasswordUpdate && (
                <>
                    <Divider sx={{ my: 2.5, borderColor: 'rgba(0,0,0,0.06)' }} />
                    <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '1rem', color: colors.textPrimary, mb: 2 }}>
                        Update Password
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <Box sx={{ flex: 1 }}>
                            <SectionLabel required>New Password</SectionLabel>
                            <TextField
                                fullWidth type={showNewPassword ? "text" : "password"}
                                placeholder="Min. 8 characters" value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start"><LockOutlinedIcon sx={{ fontSize: '1.1rem', color: colors.textTertiary }} /></InputAdornment>,
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => setShowNewPassword(!showNewPassword)} edge="end" size="small">
                                                {showNewPassword ? <VisibilityOff sx={{ fontSize: '1.1rem' }} /> : <Visibility sx={{ fontSize: '1.1rem' }} />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                sx={inputSx}
                            />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <SectionLabel required>Confirm Password</SectionLabel>
                            <TextField
                                fullWidth type={showConfirmPassword ? "text" : "password"}
                                placeholder="Confirm password" value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start"><LockOutlinedIcon sx={{ fontSize: '1.1rem', color: colors.textTertiary }} /></InputAdornment>,
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end" size="small">
                                                {showConfirmPassword ? <VisibilityOff sx={{ fontSize: '1.1rem' }} /> : <Visibility sx={{ fontSize: '1.1rem' }} />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                sx={inputSx}
                            />
                        </Box>
                    </Box>
                    {passwordError && (
                        <Alert severity="error" sx={{ mb: 2, fontFamily, borderRadius: '14px' }}>{passwordError}</Alert>
                    )}
                </>
            )}

            {/* Subscription Section */}
            {!isSubscribed && (
                <>
                    <Divider sx={{ my: 2.5, borderColor: 'rgba(0,0,0,0.06)' }} />
                    <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '1rem', color: colors.textPrimary, mb: 1 }}>
                        Stay Connected
                    </Typography>

                    <Alert severity="info" sx={{ mb: 2, fontFamily, borderRadius: '14px', bgcolor: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.12)' }}>
                        <Typography sx={{ fontFamily, fontSize: '0.8rem' }}>
                            Subscribe to receive exclusive deals, product updates, and important notifications from KlonIT.
                        </Typography>
                    </Alert>

                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <Box sx={{ flex: 1 }}>
                            <SectionLabel>Email Address</SectionLabel>
                            <TextField
                                fullWidth placeholder="email@example.com" value={subscriptionEmail}
                                onChange={(e) => setSubscriptionEmail(e.target.value)}
                                InputProps={{ startAdornment: <InputAdornment position="start"><EmailOutlinedIcon sx={{ fontSize: '1.1rem', color: colors.textTertiary }} /></InputAdornment> }}
                                sx={inputSx}
                            />
                            {emailMismatchWarning && (
                                <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#dc2626', mt: 0.5 }}>
                                    {emailMismatchWarning}
                                </Typography>
                            )}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <SectionLabel>Phone Number</SectionLabel>
                            <TextField
                                fullWidth placeholder="+1 (555) 123-4567" value={subscriptionPhone}
                                onChange={(e) => setSubscriptionPhone(e.target.value)}
                                InputProps={{ startAdornment: <InputAdornment position="start"><PhoneOutlinedIcon sx={{ fontSize: '1.1rem', color: colors.textTertiary }} /></InputAdornment> }}
                                sx={inputSx}
                            />
                        </Box>
                    </Box>

                    <Typography sx={{ fontFamily, fontSize: '0.75rem', color: colors.textTertiary, mb: 1.5 }}>
                        * Provide at least email or phone number
                    </Typography>

                    <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', color: colors.textPrimary, mb: 1 }}>
                        I consent to receive SMS notifications from KlonIT for:
                    </Typography>

                    <Box sx={{ mb: 1.5 }}>
                        <FormControlLabel
                            control={<Checkbox checked={promotionalConsent} onChange={(e) => setPromotionalConsent(e.target.checked)} size="small" sx={{ '&.Mui-checked': { color: theme.palette.primary.main } }} />}
                            label={
                                <Box>
                                    <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.8rem', color: colors.textPrimary }}>Promotional offers</Typography>
                                    <Typography sx={{ fontFamily, fontSize: '0.75rem', color: colors.textTertiary }}>
                                        Receive notifications about special promotions, discounts, and limited-time offers
                                    </Typography>
                                </Box>
                            }
                        />
                    </Box>

                    <Box sx={{ mb: 1.5 }}>
                        <FormControlLabel
                            control={<Checkbox checked={exclusiveDealsConsent} onChange={(e) => setExclusiveDealsConsent(e.target.checked)} size="small" sx={{ '&.Mui-checked': { color: theme.palette.primary.main } }} />}
                            label={
                                <Box>
                                    <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.8rem', color: colors.textPrimary }}>General Information</Typography>
                                    <Typography sx={{ fontFamily, fontSize: '0.75rem', color: colors.textTertiary }}>
                                        Any type of notifications including updates, newsletters, and important announcements
                                    </Typography>
                                </Box>
                            }
                        />
                    </Box>

                    <Typography sx={{ fontFamily, fontSize: '0.7rem', color: colors.textTertiary, mb: 1.5 }}>
                        Message frequency may vary. Message and data rates may apply.
                        Reply STOP to unsubscribe at any time. Reply HELP for assistance.
                    </Typography>

                    <Box sx={{ mb: 2 }}>
                        <FormControlLabel
                            control={<Checkbox checked={termsConsent} onChange={(e) => setTermsConsent(e.target.checked)} size="small" sx={{ '&.Mui-checked': { color: theme.palette.primary.main } }} />}
                            label={
                                <Typography sx={{ fontFamily, fontSize: '0.8rem', color: colors.textSecondary }}>
                                    I agree to the{" "}
                                    <MuiLink href="https://klonit.ai/terms-of-service/" target="_blank" rel="noopener noreferrer" sx={{ fontWeight: 600 }}>
                                        Terms and Conditions
                                    </MuiLink>
                                    {" "}and{" "}
                                    <MuiLink href="https://klonit.ai/privacy-policy/" target="_blank" rel="noopener noreferrer" sx={{ fontWeight: 600 }}>
                                        Privacy Policy
                                    </MuiLink>
                                </Typography>
                            }
                        />
                    </Box>

                    <Alert severity="info" icon={false} sx={{ mb: 2, fontFamily, borderRadius: '14px', bgcolor: 'rgba(0,0,0,0.02)', border: '1px solid rgba(0,0,0,0.06)' }}>
                        <Typography sx={{ fontFamily, fontSize: '0.75rem', color: colors.textSecondary }}>
                            <strong>Privacy Notice:</strong> We will never share, sell, or rent your contact information to third parties for marketing purposes.
                        </Typography>
                    </Alert>

                    <Button
                        fullWidth onClick={handleJoinSubscription}
                        disabled={!isJoinEnabled() || isSubscribing}
                        sx={{
                            fontFamily, textTransform: 'none', fontWeight: 600, fontSize: '0.875rem',
                            color: '#fff', borderRadius: '14px', py: 1.25,
                            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                            boxShadow: `0 4px 16px ${theme.palette.primary.main}40`,
                            '&:hover': {
                                background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                                boxShadow: `0 6px 20px ${theme.palette.primary.main}50`,
                                transform: 'translateY(-1px)',
                            },
                            '&:disabled': { opacity: 0.5, background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`, color: '#fff' },
                            transition: 'all 0.2s ease',
                        }}
                    >
                        {isSubscribing ? <CircularProgress size={20} color="inherit" /> : "Join"}
                    </Button>

                    {subscriptionMessage && (
                        <Alert
                            severity={subscriptionMessage.includes('Successfully') ? 'success' : 'warning'}
                            sx={{ mt: 2, fontFamily, borderRadius: '14px' }}
                        >
                            {subscriptionMessage}
                        </Alert>
                    )}
                </>
            )}

            {isSubscribed && (
                <Alert severity="success" sx={{ mt: 2, fontFamily, borderRadius: '14px' }}>
                    {subscriptionMessage || "You are subscribed to notifications."}
                </Alert>
            )}

            {message && (
                <Alert
                    severity={message.toLowerCase().includes('error') || message.toLowerCase().includes('invalid') ? 'error' : 'info'}
                    sx={{ mt: 2, fontFamily, borderRadius: '14px' }}
                >
                    {message}
                </Alert>
            )}
        </Box>
    )
});

export default ProfilePage;
